/**
 * Imprimir el recibo en CUALQUIER navegador.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  EL PROBLEMA QUE ESTO RESUELVE, Y CUÁNTO COSTÓ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Softfi tenía dos formas de imprimir:
 *
 *   1. **Directa** (`escpos.ts`): le habla a la impresora por WebUSB o Web
 *      Bluetooth, byte a byte. Es la mejor — sin diálogos y con corte de papel.
 *      Pero **WebUSB solo existe en Chrome, Edge y Opera**. En Firefox y en
 *      Safari no existe, y no va a existir: las dos empresas dijeron que no lo
 *      van a implementar.
 *
 *   2. **El diálogo del navegador**: `window.print()` sobre la propia página,
 *      escondiendo la aplicación con CSS.
 *
 * En Firefox solo queda la 2, y la 2 estaba mal hecha: imprimía **el documento
 * entero** con la aplicación escondida pero presente, y salían hojas en blanco
 * detrás del recibo. Un negocio con Firefox veía «Este navegador no permite
 * hablar con impresoras» y papel en blanco. Se perdió un cliente por esto.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LA SOLUCIÓN: UN DOCUMENTO NUEVO CON SOLO EL TICKET
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * En vez de esconder la aplicación, se construye **otro documento** que
 * contiene únicamente el recibo, y se imprime ese. Ahí no hay nada que
 * esconder, así que no hay nada que pueda salir en blanco: el papel mide
 * exactamente lo que mide el ticket.
 *
 * Va en un `<iframe>` y no en `window.open()` a propósito: una ventana nueva la
 * bloquea el navegador la mitad de las veces, y cuando no la bloquea parpadea
 * en la cara del cajero. El `iframe` no se ve, no lo bloquea nadie, y funciona
 * igual en Chrome, Firefox, Safari y Edge.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ SE COPIAN LAS HOJAS DE ESTILO DE LA PÁGINA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El recibo se copia del DOM tal cual está — con sus clases de Tailwind y sus
 * variables de color. En un documento vacío esas clases no significan nada y el
 * ticket saldría como texto suelto sin alinear.
 *
 * Copiando los estilos de la página, el papel sale **idéntico a lo que se ve en
 * pantalla**. Y sigue habiendo una sola fuente: si mañana se le añade una línea
 * al recibo, aparece aquí sin tocar este archivo.
 */

/** Cuánto se espera a que el iframe cargue sus estilos antes de imprimir. */
const ESPERA_DE_ESTILOS = 350;

export type FormatoDeHoja = "58" | "80" | "carta";

/**
 * El ancho del papel de cada formato, en milímetros. `null` = hoja de oficina.
 *
 * ⚠️ SOLO EL ANCHO, Y EL LARGO SE MIDE DESPUÉS. Aquí está la parte que casi se
 * me escapa:
 *
 *     @page { size: 80mm auto }   ← NO ES CSS VÁLIDO
 *
 * `size` acepta `auto`, o una medida, o dos medidas — pero **no se puede
 * mezclar una medida con `auto`**. El navegador descarta la regla ENTERA y se
 * queda con el tamaño por defecto: una hoja A4.
 *
 * Y una hoja A4 en un rollo de tickets son casi 30 cm de papel: sale el recibo
 * arriba y detrás 25 cm en blanco hasta completar la «hoja». Eso es lo que
 * parece una impresora volviéndose loca.
 *
 * Lo peor es que no da ningún error: la regla simplemente no existe.
 *
 * Por eso el largo se MIDE —cuánto ocupa el ticket ya dibujado— y se declara
 * una hoja exacta: `size: 80mm 91mm`. El papel sale del tamaño del recibo y ni
 * un milímetro más.
 */
const ANCHO_MM: Record<FormatoDeHoja, number | null> = {
  "58": 58,
  "80": 80,
  carta: null,
};

/** Un milímetro son 96/25,4 píxeles de CSS. La regla de tres de siempre. */
const PX_POR_MM = 96 / 25.4;

/**
 * Cuánto mide de ancho el marco donde se dibuja el ticket para medirlo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTO NO ES UN DETALLE. AQUÍ ESTUVO EL SEGUNDO FALLO DE IMPRESIÓN.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El marco era de **1 píxel de ancho**, porque solo hacía falta que existiera,
 * no que se viera. Y ahí el navegador dibuja el ticket dentro de un píxel: cada
 * palabra se parte letra por letra y la columna se vuelve larguísima.
 *
 * Medido de verdad: un ticket de tres productos daba `scrollHeight` de 1068 px,
 * o sea **287 mm**. Y con eso se declaraba `@page { size: 80mm 287mm }`.
 *
 * Resultado en una impresora térmica: por cada recibo, casi treinta
 * centímetros de rollo — el ticket arriba y el resto en blanco. Que es
 * exactamente lo que se vio salir de la facturadora.
 *
 * Y lo peor: la regla ya era CSS válido, así que no había ningún error en
 * ningún sitio. El número simplemente estaba mal.
 *
 * Con el marco del ancho del papel de verdad, el ticket se dibuja como se va a
 * imprimir y lo que se mide es lo que mide.
 */
const ANCHO_DEL_MARCO_PX: Record<FormatoDeHoja, number> = {
  "58": Math.round(58 * PX_POR_MM),   // 219
  "80": Math.round(80 * PX_POR_MM),   // 302
  // Carta menos los 10 mm de margen de cada lado: 8,5" = 216 mm.
  carta: Math.round((216 - 20) * PX_POR_MM),
};

/**
 * Una hoja carta, en píxeles de CSS: 8,5 × 11 pulgadas a 96 por pulgada.
 * La usan los papeles que se arman YA repartidos en hojas (`imprimirHojasCarta`).
 */
export const HOJA_CARTA_PX = { ancho: 816, alto: 1056 } as const;

/** La clase de cada hoja de un papel repartido en hojas carta. */
export const CLASE_HOJA_CARTA = "hoja-carta";

/**
 * Las hojas carta en el iPad y el iPhone: el margen lo pone el papel, no la hoja.
 *
 * ⚠️ AHÍ SALÍA EN DOS HOJAS (2026-10-19, una cotización de tres productos, en
 * una capacitación con una tablet). Safari en el iPad no hace caso del
 * `@page { margin: 0 }`: le pone al papel el margen de la impresora, y la hoja
 * —que mide casi las 11 pulgadas enteras, con su margen dentro— ya no cabe: el
 * pie pasaba a una segunda hoja.
 *
 * Ahí la hoja va sin su margen de arriba y abajo (lo pone el papel) y más baja
 * que lo que queda: 10,1 pulgadas, que caben aunque la impresora deje medio
 * pulgada arriba y abajo. Lo de dentro mide lo mismo que en la pantalla
 * (728 px de ancho y más alto que lo que se repartió), así que cada fila cae
 * donde se midió. `-webkit-touch-callout` solo existe en el iPad y el iPhone:
 * en las computadoras y en Android el papel sale igual que siempre.
 */
const HOJA_CARTA_EN_IOS = `
  @supports (-webkit-touch-callout: none) {
    @page { size: Letter; margin: 0.3in; }
    .${CLASE_HOJA_CARTA} {
      width: ${HOJA_CARTA_PX.ancho - 72}px !important;
      height: 10.1in !important;
      padding: 0 8px !important;
    }
  }`;

/**
 * Imprime un papel que ya viene repartido en hojas carta: una hoja en pantalla
 * es una hoja en la impresora (lo pidió el dueño con la nota de traslado,
 * 2026-09-29: «tiene que verse como una hoja completa, y si pasa de la hoja
 * carta tienen que salir 2»).
 *
 * ⚠️ A diferencia de `imprimirHoja(…, "carta")`, aquí NO se toca la letra ni
 * los márgenes: las hojas se repartieron midiendo lo que se ve, y cambiar el
 * tamaño de la letra al imprimir movería los cortes. El margen va DENTRO de
 * cada hoja, así que el papel se pide sin margen (y de paso el navegador no
 * le pone la dirección de la página ni la fecha arriba y abajo).
 */
export function imprimirHojasCarta(idDelElemento: string): boolean {
  const original = document.getElementById(idDelElemento);
  if (!original) return false;

  const estilos = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((n) => n.outerHTML)
    .join("\n");

  const marco = document.createElement("iframe");
  marco.setAttribute("aria-hidden", "true");
  marco.style.cssText = `position:fixed;left:-10000px;top:0;width:${HOJA_CARTA_PX.ancho}px;height:400px;border:0;`;
  document.body.appendChild(marco);

  const doc = marco.contentDocument;
  if (!doc) {
    marco.remove();
    return false;
  }

  doc.open();
  doc.write(`<!doctype html>
<html lang="es" data-papel>
<head>
<meta charset="utf-8">
${estilos}
<style>
  @page { size: Letter; margin: 0; }
  html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
  /* En la pantalla la vista va reducida (transform): el papel, a tamaño real. */
  #${idDelElemento} { display: block !important; gap: 0 !important; padding: 0 !important; transform: none !important; width: auto !important; background: #fff !important; }
  /* Un pelo menos que la hoja: un píxel de más saca una hoja en blanco. */
  .${CLASE_HOJA_CARTA} {
    margin: 0 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    height: 10.95in !important;
    overflow: hidden !important;
    break-after: page;
  }
  .${CLASE_HOJA_CARTA}:last-child { break-after: auto; }
  ${HOJA_CARTA_EN_IOS}
</style>
</head>
<body>${original.outerHTML}</body>
</html>`);
  doc.close();

  const lanzar = () => {
    try {
      marco.contentWindow?.focus();
      marco.contentWindow?.print();
    } finally {
      // Ver `imprimirHoja`: en Firefox y Safari `print()` no bloquea.
      setTimeout(() => marco.remove(), 60_000);
    }
  };
  if (marco.contentWindow) setTimeout(lanzar, ESPERA_DE_ESTILOS);
  return true;
}

/**
 * Abre las hojas carta en una pestaña aparte, como el PDF de los rollos.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ UNA PESTAÑA Y NO EL MARCO ESCONDIDO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo pidió el dueño (2026-09-29) al ver una hoja carta salir en blanco. Los
 * rollos se abren en su propia pestaña (un `blob:`) y nunca fallaron; la hoja
 * carta se imprimía desde un marco escondido dentro de la aplicación, con sus
 * estilos, y una regla del ticket térmico la escondía (ver `data-papel` en
 * `index.css`).
 *
 * Ahora la hoja es un documento propio: se ve entera en su pestaña, el diálogo
 * de imprimir se abre solo y queda el botón «Imprimir» para repetir. Desde el
 * diálogo también se guarda como PDF.
 *
 * Si el navegador bloquea la pestaña, se imprime por el marco de siempre:
 * nunca se queda sin salida.
 */
export function abrirHojasCarta(idDelElemento: string): boolean {
  const original = document.getElementById(idDelElemento);
  if (!original) return false;

  try {
    const url = URL.createObjectURL(
      new Blob([documentoDeHojas(original, idDelElemento)], { type: "text/html;charset=utf-8" }),
    );
    // ⚠️ Sin `noopener`: con él, `window.open` devuelve `null` aunque la
    // pestaña se abra, y se imprimiría dos veces (ver `imprimirPdf` en el
    // recibo).
    const pestana = window.open(url, "_blank");
    if (!pestana) {
      URL.revokeObjectURL(url);
      return imprimirHojasCarta(idDelElemento);
    }
    // Tarde, no en el acto: soltarla enseguida deja la pestaña en blanco.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return true;
  } catch {
    return imprimirHojasCarta(idDelElemento);
  }
}

function escaparHtml(texto: string): string {
  return texto.replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c,
  );
}

/** El documento de la pestaña: las hojas, los estilos de la página y una barra. */
function documentoDeHojas(original: HTMLElement, id: string): string {
  // ⚠️ Direcciones completas. Un documento `blob:` no tiene carpeta propia: un
  // «/assets/…» o un «/api/…» no llevaría a ningún sitio, y la hoja saldría
  // sin estilos y sin logo.
  const copia = original.cloneNode(true) as HTMLElement;
  const imagenes = original.querySelectorAll("img");
  copia.querySelectorAll("img").forEach((img, i) => {
    const fuente = imagenes[i]?.currentSrc || imagenes[i]?.src;
    if (fuente) img.setAttribute("src", fuente);
  });
  // La escala de la pantalla no pasa al papel: la pestaña pone la suya.
  copia.style.removeProperty("transform");
  copia.style.removeProperty("transform-origin");
  copia.style.removeProperty("width");

  // Los mismos estilos de la página: las hojas se repartieron midiéndolas con
  // ellos, y así cada fila ocupa lo mismo que en pantalla.
  const estilos = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((n) =>
      n instanceof HTMLLinkElement
        ? `<link rel="stylesheet" href="${escaparHtml(n.href)}">`
        : n.outerHTML,
    )
    .join("\n");
  const titulo = escaparHtml(original.dataset.titulo || document.title);
  const letra = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

  return `<!doctype html>
<html lang="es" data-papel>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${titulo}</title>
${estilos}
<style>
  @page { size: Letter; margin: 0; }
  html, body { margin: 0 !important; padding: 0 !important; }
  body { background: #e5e7eb !important; }
  .barra-del-papel {
    position: sticky; top: 0; z-index: 10;
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 10px 16px; background: #fff; border-bottom: 1px solid #d1d5db;
    color: #111827; font: 14px/1.4 ${letra};
  }
  .barra-del-papel strong { display: block; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .barra-del-papel small { display: block; color: #4b5563; font-size: 12px; }
  .barra-del-papel button {
    flex-shrink: 0; border: 0; border-radius: 8px; padding: 10px 18px;
    background: #245c74; color: #fff; font: 600 14px/1 ${letra}; cursor: pointer;
  }
  #${id} {
    display: flex !important; flex-direction: column; align-items: center; gap: 16px;
    padding: 24px 0 !important;
  }
  .${CLASE_HOJA_CARTA} { flex-shrink: 0; }
  @media print {
    body { background: #fff !important; }
    .barra-del-papel { display: none !important; }
    .marco-del-papel { height: auto !important; padding: 0 !important; overflow: visible !important; }
    #${id} { display: block !important; gap: 0 !important; padding: 0 !important; transform: none !important; width: auto !important; }
    /* Un pelo menos que la hoja: un píxel de más saca una hoja en blanco. */
    .${CLASE_HOJA_CARTA} {
      margin: 0 !important; box-shadow: none !important; border-radius: 0 !important;
      height: 10.95in !important; overflow: hidden !important; break-after: page;
    }
    .${CLASE_HOJA_CARTA}:last-child { break-after: auto; }
    ${HOJA_CARTA_EN_IOS}
  }
</style>
</head>
<body>
<div class="barra-del-papel">
  <div style="min-width: 0">
    <strong>${titulo}</strong>
    <small>Para guardarlo como PDF, elija «Guardar como PDF» en Destino.</small>
  </div>
  <button type="button" id="imprimir-el-papel">Imprimir</button>
</div>
<div class="marco-del-papel">${copia.outerHTML}</div>
<script>
  (function () {
    // En un celular la hoja se ve entera, reducida; al papel va a tamaño real.
    // Con \`transform\` y no con \`zoom\`: en Safari el \`zoom\` agranda la letra
    // de más y la hoja se descuadra (ver \`HojaCarta\`). Como \`transform\` no
    // achica el sitio que ocupa, el marco se queda con el alto reducido.
    var marco = document.querySelector(".marco-del-papel");
    var papel = document.getElementById(${JSON.stringify(id)});
    function ajustar() {
      var escala = Math.max(0.25, Math.min(1, (window.innerWidth - 16) / ${HOJA_CARTA_PX.ancho}));
      var reducida = escala < 1;
      papel.style.width = reducida ? "${HOJA_CARTA_PX.ancho}px" : "";
      papel.style.transformOrigin = "0 0";
      papel.style.transform = reducida ? "scale(" + escala + ")" : "";
      marco.style.overflow = reducida ? "hidden" : "";
      marco.style.paddingLeft = reducida ? "8px" : "";
      marco.style.height = reducida ? papel.offsetHeight * escala + "px" : "";
    }
    ajustar();
    window.addEventListener("resize", ajustar);
    document.getElementById("imprimir-el-papel").addEventListener("click", function () {
      window.print();
    });
    // Con el logo y los estilos ya cargados, el diálogo se abre solo.
    window.addEventListener("load", function () {
      setTimeout(function () { window.print(); }, 300);
    });
  })();
</script>
</body>
</html>`;
}

/**
 * Imprime el elemento indicado, y solo ese, con el tamaño de papel pedido.
 *
 * Devuelve `false` si el elemento no está en la página — nunca lanza: dejar al
 * cajero con un error rojo en la cara después de cobrar sería lo peor posible.
 */
export function imprimirHoja(
  idDelElemento: string,
  formato: FormatoDeHoja = "80",
): boolean {
  const original = document.getElementById(idDelElemento);
  if (!original) return false;

  // Los estilos de la página, para que el ticket salga igual que en pantalla.
  const estilos = Array.from(
    document.querySelectorAll('style, link[rel="stylesheet"]'),
  )
    .map((n) => n.outerHTML)
    .join("\n");

  const marco = document.createElement("iframe");
  // Fuera de la vista pero NO `display:none`: un iframe oculto del todo no
  // termina de calcular el diseño en algunos navegadores, y se imprimiría un
  // ticket a medio dibujar.
  //
  // ⚠️ Y CON EL ANCHO DEL PAPEL DE VERDAD. Ver `ANCHO_DEL_MARCO_PX`: con un
  // marco de 1 píxel, el ticket se dibuja partido letra a letra y lo que se
  // mide no tiene nada que ver con lo que se imprime.
  //
  // Se esconde moviéndolo fuera de la pantalla y no con `opacity:0` en una
  // esquina: 302 píxeles pegados al borde derecho pueden sacar una barra de
  // desplazamiento horizontal en la página de debajo.
  marco.setAttribute("aria-hidden", "true");
  marco.style.cssText =
    `position:fixed;left:-10000px;top:0;width:${ANCHO_DEL_MARCO_PX[formato]}px;` +
    // Sin `visibility:hidden`: fuera de la pantalla ya no se ve, y esconderlo
    // además con visibilidad es arriesgarse a que algún navegador se salte el
    // cálculo del diseño — que es justo lo que hay que medir.
    "height:400px;border:0;";
  document.body.appendChild(marco);

  const doc = marco.contentDocument;
  if (!doc) {
    marco.remove();
    return false;
  }

  doc.open();
  doc.write(`<!doctype html>
<html lang="es" data-papel>
<head>
<meta charset="utf-8">
${estilos}
<style>
  /* El @page de verdad se añade abajo, cuando ya se puede medir el ticket. */
  @page { margin: 0; }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
  /* El ticket, en negro sobre blanco y ocupando el papel entero. La impresora
     térmica no imprime color: un gris claro saldría casi invisible. */
  #${idDelElemento} {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 2mm 3mm !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: #fff !important;
    color: #000 !important;
    font-size: ${formato === "58" ? "9pt" : "10pt"} !important;
    line-height: 1.25 !important;
  }
  /* Que una línea del ticket no se parta entre dos trozos de papel. */
  #${idDelElemento} > div, #${idDelElemento} > p { break-inside: avoid; }
</style>
</head>
<body>${original.outerHTML}</body>
</html>`);
  doc.close();

  const lanzar = () => {
    try {
      // ══════════════════════════════════════════════════════════════════
      //  LA HOJA SE DECLARA DEL TAMAÑO EXACTO DEL TICKET
      // ══════════════════════════════════════════════════════════════════
      //
      // Hasta aquí el documento no tiene tamaño de hoja, así que ya está
      // dibujado y se puede medir. Ver el comentario de `ANCHO_MM`: no existe
      // forma de pedir «ancho fijo y largo automático», hay que dar los dos.
      const anchoMm = ANCHO_MM[formato];
      if (anchoMm !== null) {
        const cuerpo = doc.body;
        // 96 px CSS = 25,4 mm. Se redondea hacia arriba y se dejan 4 mm de
        // sobra: quedarse corto CORTA el final del ticket —justo donde va el
        // total— y eso es peor que un dedo de papel de más.
        const altoMm = Math.ceil((cuerpo.scrollHeight * 25.4) / 96) + 4;
        const regla = doc.createElement("style");
        regla.textContent = `@page { size: ${anchoMm}mm ${altoMm}mm; margin: 0; }`;
        doc.head.appendChild(regla);
      } else {
        const regla = doc.createElement("style");
        regla.textContent = "@page { size: Letter; margin: 10mm; }";
        doc.head.appendChild(regla);
      }

      marco.contentWindow?.focus();
      marco.contentWindow?.print();
    } finally {
      // ⚠️ Se quita DESPUÉS de un momento, no en el acto.
      //
      // En Firefox y en Safari, `print()` no bloquea: vuelve enseguida y el
      // diálogo sigue abierto. Quitando el iframe al instante, el navegador se
      // queda sin el documento que iba a imprimir y sale una hoja en blanco —
      // que es justo lo que se está arreglando.
      setTimeout(() => marco.remove(), 60_000);
    }
  };

  // Se espera a que carguen los estilos copiados. Sin esto el ticket sale sin
  // formato en el primer intento y bien en el segundo, que es el peor tipo de
  // fallo: parece que se arregla solo.
  if (marco.contentWindow) {
    setTimeout(lanzar, ESPERA_DE_ESTILOS);
  }
  return true;
}
